import { computed, onBeforeUnmount, ref, watch } from 'vue'
import type { ComputedRef, Ref } from 'vue'

// Shared across every instance that opts in with `singleOpen`, so that only one such
// popover is open at a time on the page. Cleared when the owning component unmounts.
const openId = ref<string | null>(null)
let counter = 0

export type OutsideEventType = 'pointerdown' | 'mousedown' | 'touchstart' | 'click'

export interface PopoverPositionOptions {
  /** Preferred popover width in px; shrinks to fit the viewport. */
  width: number
  /** Minimum distance in px between the popover and the viewport edge. */
  margin: number
  /** Distance in px between the bottom of the anchor and the popover. */
  gap: number
}

export interface PopoverPosition {
  top: number
  left: number
  width: number
}

export interface DismissablePopoverOptions {
  /** Prefix for the generated id (useful for aria-controls / aria-labelledby). */
  idPrefix?: string
  /** The trigger element. Counts as "inside" and receives focus when focus is returned. */
  anchorRef?: Ref<HTMLElement | null>
  /** The popover element. Counts as "inside"; scrolling within it never closes the popover. */
  popoverRef?: Ref<HTMLElement | null>
  /** An extra wrapper element that also counts as "inside" for the outside-event check. */
  boundaryRef?: Ref<HTMLElement | null>
  /** Keep one open at a time across all instances that opt in. */
  singleOpen?: boolean
  /** Close on Escape. */
  closeOnEscape?: boolean
  /**
   * Handle Escape in the document capture phase and stop its propagation, so an enclosing
   * slide-over or dialog bubble-phase handler never sees it. Requires closeOnEscape.
   */
  escapeCapture?: boolean
  /** Move focus back to the anchor when Escape closes the popover. */
  returnFocusOnEscape?: boolean
  /** Document events whose target lies outside the anchor, popover and boundary close the popover. */
  closeOnOutside?: readonly OutsideEventType[]
  /** Close when the page or any scroll container scrolls (scrolling inside the popover is ignored). */
  closeOnScroll?: boolean
  /** Close when the window is resized. */
  closeOnResize?: boolean
  /** Called for every keydown that did not close the popover (e.g. arrow-key navigation). */
  onKeydown?: (event: KeyboardEvent) => void
  /** Enables viewport-clamped positioning, exposed as `position` and refreshed on every open. */
  position?: PopoverPositionOptions
}

export interface CloseOptions {
  returnFocus?: boolean
}

export interface DismissablePopover {
  id: string
  isOpen: ComputedRef<boolean>
  position: Ref<PopoverPosition>
  open: () => void
  close: (options?: CloseOptions) => void
  toggle: () => void
}

export function useDismissablePopover(options: DismissablePopoverOptions = {}): DismissablePopover {
  counter += 1
  const id = `${options.idPrefix ?? 'popover'}-${counter}`
  const localOpen = ref(false)
  const position = ref<PopoverPosition>({ top: 0, left: 0, width: options.position?.width ?? 0 })
  const outsideEvents = options.closeOnOutside ?? []
  const escapeCapture = options.escapeCapture === true

  const isOpen = computed<boolean>(() => (options.singleOpen ? openId.value === id : localOpen.value))

  function place(): void {
    const settings = options.position
    const anchor = options.anchorRef?.value
    if (settings === undefined || anchor === null || anchor === undefined) return
    const rect = anchor.getBoundingClientRect()
    const width = Math.min(settings.width, window.innerWidth - settings.margin * 2)
    const centred = rect.left + rect.width / 2 - width / 2
    const left = Math.max(settings.margin, Math.min(centred, window.innerWidth - width - settings.margin))
    position.value = { top: rect.bottom + settings.gap, left, width }
  }

  function open(): void {
    place()
    if (options.singleOpen) openId.value = id
    else localOpen.value = true
  }

  function close(closeOptions: CloseOptions = {}): void {
    if (options.singleOpen) {
      if (openId.value === id) openId.value = null
    } else {
      localOpen.value = false
    }
    if (closeOptions.returnFocus) options.anchorRef?.value?.focus()
  }

  function toggle(): void {
    if (isOpen.value) close()
    else open()
  }

  function isInside(target: Node): boolean {
    return [options.anchorRef, options.popoverRef, options.boundaryRef].some(
      (element) => element?.value?.contains(target) === true,
    )
  }

  function onKeydown(event: KeyboardEvent): void {
    if (options.closeOnEscape && event.key === 'Escape') {
      if (escapeCapture) event.stopPropagation()
      close({ returnFocus: options.returnFocusOnEscape })
      return
    }
    options.onKeydown?.(event)
  }

  function onOutsideEvent(event: Event): void {
    const target = event.target
    if (!(target instanceof Node) || isInside(target)) return
    close()
  }

  // Scroll events do not bubble, so this runs in the capture phase; scrolling inside the popover is ignored.
  function onScroll(event: Event): void {
    // The target is the window itself for page scrolls, which is not a Node.
    const target = event.target
    if (target instanceof Node && options.popoverRef?.value?.contains(target)) return
    close()
  }

  function onResize(): void {
    close()
  }

  const listensToKeys = options.closeOnEscape === true || options.onKeydown !== undefined

  let listening = false

  function addListeners(): void {
    if (listening) return
    listening = true
    if (listensToKeys) document.addEventListener('keydown', onKeydown, escapeCapture)
    for (const type of outsideEvents) document.addEventListener(type, onOutsideEvent)
    if (options.closeOnResize) window.addEventListener('resize', onResize)
    if (options.closeOnScroll) window.addEventListener('scroll', onScroll, true)
  }

  function removeListeners(): void {
    if (!listening) return
    listening = false
    document.removeEventListener('keydown', onKeydown, escapeCapture)
    for (const type of outsideEvents) document.removeEventListener(type, onOutsideEvent)
    window.removeEventListener('resize', onResize)
    window.removeEventListener('scroll', onScroll, true)
  }

  // Synchronous so listeners are in place the moment the popover opens, and gone the moment it closes.
  watch(
    isOpen,
    (opened) => {
      if (opened) addListeners()
      else removeListeners()
    },
    { flush: 'sync' },
  )

  onBeforeUnmount(() => {
    removeListeners()
    close()
  })

  return { id, isOpen, position, open, close, toggle }
}

/** Clears the shared single-open state. Intended for test setup/teardown only. */
export function resetDismissablePopovers(): void {
  openId.value = null
}
